/**
 * iframe 消息管理器
 * 用于在路由守卫中处理来自 iframe 的 SSO 登录消息
 */

type MessageHandler = (key: string, value: any) => void;

class IframeMessageManager {
  private messageQueue: Array<{ key: string; value: any; timestamp: number }> =
    [];

  private handlers: Map<string, Set<MessageHandler>> = new Map();

  private maxQueueSize = 10;

  private messageTimeout = 5000; // 5秒内的消息有效

  private iframeLoadedPromise: Promise<void> | null = null;

  private iframeLoadedResolve: (() => void) | null = null;

  constructor() {
    // 初始化 iframe 加载 Promise
    this.iframeLoadedPromise = new Promise((resolve) => {
      this.iframeLoadedResolve = resolve;
    });
    // 监听来自 iframe 的消息
    window.addEventListener('message', (event) => {
      // 验证消息来源（可选，根据实际需求调整）
      if (
        event.origin !== 'https://sso-test.parametrix.cn' &&
        event.origin !== 'http://localhost:5173' &&
        event.origin !== 'https://sso.parametrix.cn' &&
        event.origin !== 'https://sso.gamebot.ai' &&
        event.origin !== 'https://sso.knit.top'
      )
        return;

      const { key, value } = event.data || {};
      if (key) {
        this.handleMessage(key, value);
      }
    });
  }

  /**
   * 处理接收到的消息
   */
  private handleMessage(key: string, value: any) {
    const message = {
      key,
      value,
      timestamp: Date.now(),
    };

    // 添加到消息队列
    this.messageQueue.push(message);
    if (this.messageQueue.length > this.maxQueueSize) {
      this.messageQueue.shift(); // 移除最旧的消息
    }

    // 触发注册的处理器
    const handlers = this.handlers.get(key);
    if (handlers) {
      handlers.forEach((handler) => handler(key, value));
    }
  }

  /**
   * 注册消息处理器
   */
  on(key: string, handler: MessageHandler) {
    if (!this.handlers.has(key)) {
      this.handlers.set(key, new Set());
    }
    this.handlers.get(key)!.add(handler);
  }

  /**
   * 取消注册消息处理器
   */
  off(key: string, handler: MessageHandler) {
    const handlers = this.handlers.get(key);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * 获取最新的指定类型的消息
   */
  getLatestMessage(key: string): { key: string; value: any } | null {
    // 清理过期消息
    const now = Date.now();
    this.messageQueue = this.messageQueue.filter(
      (msg) => now - msg.timestamp < this.messageTimeout
    );

    // 查找最新的匹配消息
    // eslint-disable-next-line no-plusplus
    for (let i = this.messageQueue.length - 1; i >= 0; i--) {
      if (this.messageQueue[i].key === key) {
        return {
          key: this.messageQueue[i].key,
          value: this.messageQueue[i].value,
        };
      }
    }
    return null;
  }

  /**
   * 等待指定类型的消息（用于路由守卫）
   * @param key 消息类型
   * @param timeout 超时时间（毫秒）
   */
  waitForMessage(
    key: string,
    timeout: number = 1000
  ): Promise<{ key: string; value: any } | null> {
    return new Promise((resolve) => {
      // 先检查是否已有消息
      const existing = this.getLatestMessage(key);

      if (existing) {
        resolve(existing);
        return;
      }

      // 等待新消息
      let resolved = false;
      const handler: MessageHandler = (msgKey, msgValue) => {
        console.log(msgKey, msgValue);
        if (msgKey === key && !resolved) {
          resolved = true;
          this.off(key, handler);
          resolve({ key: msgKey, value: msgValue });
        }
      };

      this.on(key, handler);

      // 超时处理
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          this.off(key, handler);
          resolve(null);
        }
      }, timeout);
    });
  }

  /**
   * 清除指定类型的消息
   */
  clearMessage(key: string) {
    this.messageQueue = this.messageQueue.filter((msg) => msg.key !== key);
  }

  /**
   * 标记 iframe 已加载完成
   */
  markIframeLoaded() {
    if (this.iframeLoadedResolve) {
      this.iframeLoadedResolve();
      this.iframeLoadedResolve = null;
    }
  }

  /**
   * 等待 iframe 加载完成
   * @param timeout 超时时间（毫秒），默认 5 秒
   */
  waitForIframeLoad(timeout: number = 5000): Promise<boolean> {
    if (!this.iframeLoadedPromise) {
      // 如果 Promise 已经被 resolve，创建一个新的
      return Promise.resolve(true);
    }

    return Promise.race([
      this.iframeLoadedPromise.then(() => true),
      new Promise<boolean>((resolve) => {
        setTimeout(() => resolve(false), timeout);
      }),
    ]);
  }
}

// 创建单例
export const iframeMessageManager = new IframeMessageManager();

export default null;
